import * as React from 'react';
import styled from '@emotion/styled';
import ModalInternal from 'react-modal';
import { hsl, lighten, darken } from 'polished';
import { merge } from 'lodash';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';

import './styles.css';

const grey = hsl(210, 0.03, 0.85);

const StyledIcon = styled(Icon)`
  font-size: 1.5rem;
  position: absolute;
  top: 20px;
  right: 20px;
  color: ${grey};
  
  &:hover {
    cursor: pointer;
    color: ${darken(0.15, grey)};
  }

  &:active {
    color: ${lighten(0.05, grey)};
  }
`;

const BASE_STYLE: ModalInternal.Styles = {
  content: {
    borderRadius: 10,
    border: 'none',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    padding: 'none',
    width: 900,
    height: 630,
  },
  overlay: {
    zIndex: 10001,
    backgroundColor: '#9a9a9ab7',
  },
};

export interface ModalProps extends ModalInternal.Props {
  overlayStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  withClose?: boolean;
  children: React.ReactChildren | React.ReactChild;
  onClose: () => void;
}

function Modal(props: ModalProps) {
  const {
    isOpen = true,
    onClose,
    children,
    overlayStyle = {},
    contentStyle = {},
    withClose = true,
    ...rest
  } = props;

  React.useEffect(() => {
    ModalInternal.setAppElement('#modal');
  }, []);

  const style: ModalInternal.Styles = React.useMemo(() => ({
    content: merge(BASE_STYLE.content, contentStyle),
    overlay: merge(BASE_STYLE.overlay, overlayStyle),
  }), [contentStyle, overlayStyle]);

  const closeIcon = React.useMemo(() => (
    withClose
      ? <StyledIcon icon={faTimes} onClick={onClose} aria-label="Close modal" />
      : null
  ), [withClose, onClose]);

  return (
    <ModalInternal
        isOpen={isOpen}
        onRequestClose={onClose}
        style={style}
        {...rest}
    >
      { closeIcon }
      { children }
    </ModalInternal>
  );
}

export default Modal;
